import { useMutation, useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDock, miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { RitualType, SubTaskType } from '../client/generated/graphql';
import { createJournalEntryCache, createJournalEntryMutation } from '../client/mutations/createJournalEntry';
import { updateJournalEntryMutation } from '../client/mutations/updateJournalEntry';
import { updateQuestMutation } from '../client/mutations/updateQuest';
import { getJournalEntryQuery } from '../client/queries/journalEntry';
import { Page } from '../components/Page';
import { RatingHeaderBanner } from '../components/rating/RatingHeaderBanner';
import { RatingPrompt } from '../components/rating/RatingPrompt';
import { RatingSelector } from '../components/rating/RatingSelector';
import { RatingEntryPageSkeleton } from '../components/rating/skeleton/RatingEntryPageSkeleton';
import { getRitualConfig } from '../helpers/ritual';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedQuestWithFallback } from '../hooks/caching/quest';

export const RatingEntryPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ritualId: ritualIdParam, ratingEntryId: ratingEntryIdParam } = useParams<{ ritualId?: string; ratingEntryId?: string }>();
    const [rating, setRating] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);

    const { quest, loading: questLoading } = useCachedQuestWithFallback(ritualIdParam);

    const ratingTask = useMemo(() => {
        return quest?.subTasks?.find(t => t.taskType === SubTaskType.Rating);
    }, [quest?.subTasks]);

    const ratingEntryId = ratingEntryIdParam || ratingTask?.ratingEntryId || ratingTask?.journalEntryId;

    const { data: journalEntryData, loading: journalEntryLoading } = useQuery(
        getJournalEntryQuery,
        {
            variables: { id: ratingEntryId! },
            skip: !ratingEntryId,
            fetchPolicy: 'cache-first',
        }
    );

    const [createJournalEntry] = useMutation(createJournalEntryMutation, {
        update: createJournalEntryCache,
    });
    const [updateJournalEntry] = useMutation(updateJournalEntryMutation);
    const [updateQuest] = useMutation(updateQuestMutation);

    const existingEntry = journalEntryData?.journalEntry;
    const isEditing = !!existingEntry;

    const ritualType = quest?.ritualType as RitualType | undefined;
    const config = useMemo(
        () => {
            const baseConfig = ritualType ? getRitualConfig(t)[ritualType] : null;
            return baseConfig ? { ...baseConfig, icon: 'star' } : null;
        },
        [ritualType, t],
    );

    const taskName = ratingTask?.name;
    const promptText = ratingTask?.promptText;

    useEffect(() => {
        if (existingEntry?.rating != null && rating === null) {
            setRating(existingEntry.rating);
        }
    }, [existingEntry?.rating, rating]);

    const handleSave = useCallback(async () => {
        if (rating === null || !ritualIdParam || !quest) return;

        setSaving(true);
        try {
            if (isEditing && ratingEntryId) {
                await updateJournalEntry({
                    variables: {
                        id: ratingEntryId,
                        data: { rating },
                    },
                });
            } else {
                const result = await createJournalEntry({
                    variables: {
                        data: {
                            rating,
                            ritualId: ritualIdParam,
                            ritualType: ritualType ?? undefined,
                            taskName: ratingTask?.name ?? undefined,
                        },
                    },
                });

                if (result.data?.createJournalEntry && quest.subTasks) {
                    const ratingTaskIndex = quest.subTasks.findIndex(
                        t => t.taskType === SubTaskType.Rating && !t.done
                    );

                    if (ratingTaskIndex >= 0) {
                        const updatedSubTasks = quest.subTasks.map((t, i) => {
                            const task: {
                                name: string;
                                done: boolean;
                                taskType?: SubTaskType | null;
                                promptText?: string | null;
                                journalEntryId?: string | null;
                                ratingEntryId?: string | null;
                            } = {
                                name: t.name,
                                done: i === ratingTaskIndex ? true : t.done,
                            };
                            if (t.taskType) task.taskType = t.taskType;
                            if (t.promptText) task.promptText = t.promptText;
                            if (t.journalEntryId) task.journalEntryId = t.journalEntryId;
                            if (i === ratingTaskIndex && result.data?.createJournalEntry?.id) {
                                task.ratingEntryId = result.data.createJournalEntry.id;
                                task.journalEntryId = result.data.createJournalEntry.id;
                            } else if (t.ratingEntryId) {
                                task.ratingEntryId = t.ratingEntryId;
                            }
                            return task;
                        });

                        const {
                            id: questId,
                            __typename,
                            metric,
                            tracking,
                            completedOn,
                            templateId,
                            ritualType: _ritualType,
                            repeat,
                            subTasks: _,
                            ...questFields
                        } = quest as any;

                        await updateQuest({
                            variables: {
                                ...questFields,
                                questId,
                                repeat: repeat
                                    ? (({ __typename: _t, ...repeatFields }: any) => repeatFields)(repeat)
                                    : undefined,
                                subTasks: updatedSubTasks,
                            },
                        });
                    }
                }
            }

            navigate(-1);
        } catch (error) {
            console.error('Failed to save rating:', error);
        } finally {
            setSaving(false);
        }
    }, [rating, ritualIdParam, quest, ritualType, ratingTask, isEditing, ratingEntryId, createJournalEntry, updateJournalEntry, updateQuest, navigate]);

    const isValid = useMemo(() => rating !== null && !saving, [rating, saving]);

    const navigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const isLoading = questLoading || journalEntryLoading || !config;

    if (isLoading || !config) {
        return <RatingEntryPageSkeleton />;
    }

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: t('actions.cancel'), onClick: navigateBack, navigation: 'back' }}
                    main={{
                        text: isEditing ? t('actions.save') : t('journal.saveAndComplete'),
                        onClick: handleSave,
                        enabled: isValid,
                        loading: saving,
                        bgColor: isValid ? config.buttonBgEnabled : getButtonThemeColors(false).backgroundColor,
                    }}
                />
            }
        >
            <Stack height="100%" sx={{ background: 'var(--tgui--secondary_bg_color)', paddingTop: 'var(--tg-viewport-content-safe-area-inset-top, 0px)' }}>
                <RatingHeaderBanner config={config} taskName={taskName} />
                <Stack sx={{ flex: 1, padding: '16px', paddingBottom: miniButtonDockScrollBottomPad, overflow: 'auto' }}>
                    <RatingPrompt promptText={promptText} />
                    <RatingSelector rating={rating} config={config} onRatingChange={setRating} />
                </Stack>
            </Stack>
        </Page>
    );
};
